import { Action, PageShell, GetStarted } from '..';
import { PageBody, PageHero } from '../page';
import type { Page, Post } from '../../content';
import { formatDate } from '../../content';
import { pageComponents, postComponents } from '.';

/** An MDX page: the interior page template around the file's content. */
export function GuidePage({ page }: { page: Page }) {
  const { Content } = page;
  return (
    <PageShell>
      <PageBody>
        <PageHero
          title={page.heading}
          lede={page.lede}
          art={page.art}
          actions={
            page.action && (
              <Action href={page.action.href} className="s-btn s-btn-pri">
                {page.action.label}
              </Action>
            )
          }
        />
        <Content components={pageComponents} />
      </PageBody>
      <GetStarted />
    </PageShell>
  );
}

/** An MDX post: header, byline and structured data around the file's content. */
export function PostPage({ post }: { post: Post }) {
  const { Content } = post;
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    image: `https://statio.dev${post.image}`,
    author: { '@type': 'Person', name: post.author, url: 'https://logicalbytes.dev/about/' },
    publisher: { '@type': 'Organization', name: 'Statio', url: 'https://statio.dev/' },
    mainEntityOfPage: `https://statio.dev/blog/${post.slug}/`,
  };
  // <Toc /> in a post renders its ## headings, wherever the author puts it.
  const Toc = () => (
    <nav className="bl-toc" aria-label="In this post">
      <b>In this post</b>
      <ol>
        {post.toc.map((t) => (
          <li key={t.id}>
            <a href={`#${t.id}`}>{t.text}</a>
          </li>
        ))}
      </ol>
    </nav>
  );
  return (
    <PageShell>
      <article className="s-wrap bl">
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(ld) }} />
        <header className="bl-head">
          <h1>{post.title}</h1>
          {post.lede && <p className="bl-lede">{post.lede}</p>}
          <div className="bl-byline">
            <span>
              <b>{post.author}</b>
            </span>
            <span>{formatDate(post.date)}</span>
            <span>{post.readMinutes} min read</span>
          </div>
        </header>
        <div className="bl-body">
          <Content components={{ ...postComponents, Toc }} />
        </div>
      </article>
    </PageShell>
  );
}
